/**
 * Audit Assistance: full-page chat with the audit agent.
 *
 * The agent reads rule documents (pgvector), the run database (guarded SQL
 * over audit_views — including the harvested result_lines figures) and,
 * when a workbook is attached, a bounded extract of the file itself.
 * Attachments upload through the same artifact store as every other file,
 * so they are content-addressed and auditable.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { api, type AuditAnswer } from "../lib/api";
import { errorText } from "../lib/errors";
import { aiEnabled } from "../lib/states";
import { useToast } from "../hooks/useToast";
import { ID_SLICE_LEN, SS_AUDIT_CHAT_KEY } from "../lib/config";
import { usePageTitle } from "../hooks/usePageTitle";
import { FilePicker } from "../components/ui/FilePicker";
import { PageHeader } from "../components/ui/PageHeader";
import { CopyTextButton } from "../components/ui/CopyTextButton";
import { Markdown } from "../components/ui/Markdown";
import { ProposedActions } from "../components/ProposedActions";

type ChatMessage =
  | { role: "user"; text: string; attachment?: string }
  | { role: "assistant"; answer: AuditAnswer }
  | { role: "error"; text: string };

function isChatMessage(value: unknown): value is ChatMessage {
  if (typeof value !== "object" || value === null) return false;
  const role = (value as { role?: unknown }).role;
  return role === "user" || role === "assistant" || role === "error";
}

/** The conversation survives navigation but clears with the tab (sessionStorage). */
function loadStoredChat(): ChatMessage[] {
  try {
    const raw = window.sessionStorage.getItem(SS_AUDIT_CHAT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isChatMessage) : [];
  } catch {
    return [];
  }
}

export function AuditPage() {
  const { t } = useTranslation();
  usePageTitle("audit.title");
  const [searchParams, setSearchParams] = useSearchParams();
  // ?job= scopes the conversation to one run (set by "ask about this run").
  const jobContext = searchParams.get("job");
  const [messages, setMessages] = useState<ChatMessage[]>(loadStoredChat);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachment, setAttachment] = useState<{ id: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Persist the conversation across navigation (cleared with the tab).
  useEffect(() => {
    try {
      window.sessionStorage.setItem(SS_AUDIT_CHAT_KEY, JSON.stringify(messages));
    } catch {
      /* private mode / quota */
    }
  }, [messages]);

  function clearConversation() {
    setMessages([]);
    try {
      window.sessionStorage.removeItem(SS_AUDIT_CHAT_KEY);
    } catch {
      /* private mode */
    }
  }

  const attach = useCallback(async (file: File) => {
    setUploading(true);
    try {
      const result = await api.upload(file);
      setAttachment({ id: result.artifact_id, name: result.display_name });
    } catch (err) {
      setMessages((old) => [...old, { role: "error", text: errorText(err) }]);
    } finally {
      setUploading(false);
    }
  }, []);

  const toast = useToast();
  const ai = aiEnabled();

  async function send() {
    const q = question.trim();
    if (!q || busy) return;
    // the route refuses too; this only spares the operator an error
    // envelope for a service they were told was switched off
    if (!ai) {
      toast(t("common.aiDisabled"), "info");
      return;
    }
    setBusy(true);
    setQuestion("");
    setMessages((old) => [
      ...old,
      { role: "user", text: q, attachment: attachment?.name },
    ]);
    try {
      const answer = await api.ask(q, jobContext ?? undefined, attachment?.id);
      setMessages((old) => [...old, { role: "assistant", answer }]);
    } catch (err) {
      setMessages((old) => [...old, { role: "error", text: errorText(err) }]);
    } finally {
      setBusy(false);
      window.setTimeout(
        () => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }),
        50,
      );
    }
  }

  return (
    <Stack spacing={2} sx={{ height: "calc(100vh - 180px)" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <PageHeader title={t("audit.title")} subtitle={t("audit.subtitle")} />
        {messages.length > 0 && (
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={clearConversation}
            sx={{ flexShrink: 0 }}
          >
            {t("audit.clear")}
          </Button>
        )}
      </Stack>

      <Paper
        ref={scrollRef}
        variant="outlined"
        sx={{ flexGrow: 1, overflow: "auto", p: 2, bgcolor: "background.default" }}
      >
        {messages.length === 0 && (
          <Stack alignItems="center" spacing={1} sx={{ mt: 6, color: "text.disabled" }}>
            <SmartToyIcon fontSize="large" />
            <Typography variant="body2">{t("audit.empty")}</Typography>
          </Stack>
        )}
        <Stack spacing={2}>
          {messages.map((message, i) => (
            <MessageBubble key={i} message={message} />
          ))}
          {busy && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "text.secondary" }}>
              <CircularProgress size={16} />
              <Typography variant="body2">{t("audit.thinking")}</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {(attachment || jobContext) && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {jobContext && (
            <Chip
              label={t("audit.context", { id: jobContext.slice(0, ID_SLICE_LEN) })}
              onDelete={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("job");
                setSearchParams(next, { replace: true });
              }}
              deleteIcon={<CloseIcon aria-label={t("audit.contextRemove")} />}
              color="secondary"
              variant="outlined"
            />
          )}
          {attachment && (
            <Chip
              label={t("audit.attached", { name: attachment.name })}
              onDelete={() => setAttachment(null)}
              deleteIcon={<CloseIcon />}
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
      )}

      <Stack direction="row" spacing={1}>
        <FilePicker onFile={(file) => void attach(file)}>
          {(open) => (
            <Tooltip title={t("audit.attach")}>
              <span>
                <IconButton
                  onClick={open}
                  disabled={uploading}
                  color={attachment ? "primary" : "default"}
                  aria-label={t("audit.attach")}
                >
                  {uploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </FilePicker>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder={t("audit.placeholder")}
          inputProps={{ "aria-label": t("audit.placeholder") }}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <Tooltip title={t("audit.send")}>
          <span>
            <IconButton
              color={ai ? "primary" : "default"}
              onClick={() => void send()}
              disabled={busy || !question.trim()}
              aria-label={t("audit.send")}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const { t } = useTranslation();

  if (message.role === "error") {
    return <Alert severity="error">{message.text}</Alert>;
  }

  if (message.role === "user") {
    return (
      <Box sx={{ alignSelf: "flex-end", maxWidth: "75%" }}>
        <Paper sx={{ p: 1.5, bgcolor: "primary.main", color: "primary.contrastText" }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {message.text}
          </Typography>
          {message.attachment && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
            >
              <AttachFileIcon sx={{ fontSize: 14 }} /> {message.attachment}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  const { answer } = message;
  return (
    <Box sx={{ alignSelf: "flex-start", maxWidth: "85%" }}>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              variant="outlined"
              label={t(`audit.intent.${answer.intent}`, answer.intent)}
            />
            {answer.retrieval_backend !== "none" && (
              <Chip size="small" variant="outlined" label={answer.retrieval_backend} />
            )}
            {(answer.context_used ?? []).map((source) => (
              <Chip
                key={source}
                size="small"
                color="primary"
                variant="outlined"
                label={t(`audit.gathered.${source}`, source)}
              />
            ))}
          </Stack>
          <CopyTextButton value={answer.answer} label={t("audit.copyAnswer")} />
        </Stack>
        <Markdown text={answer.answer} />

        {answer.sql && answer.sql_verdict === "ALLOWED" && (
          <Accordion variant="outlined" sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="caption">{t("audit.sqlUsed")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
              >
                <Typography variant="caption" className="num" sx={{ whiteSpace: "pre-wrap" }}>
                  {answer.sql}
                </Typography>
                <CopyTextButton value={answer.sql} label={t("audit.copySql")} />
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {answer.attachment_notes && (
          <Accordion variant="outlined" sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="caption">{t("audit.readingNotes")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>
                {answer.attachment_notes}
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {answer.citations.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t("audit.sources")}:{" "}
              {answer.citations.map((c) => `${c.title} v${c.version} §${c.section}`).join(" · ")}
            </Typography>
          </Box>
        )}

        {(answer.proposed_actions?.length ?? 0) > 0 && (
          <ProposedActions actions={answer.proposed_actions ?? []} />
        )}
      </Paper>
    </Box>
  );
}

